/**
 * offlineStorage — IndexedDB-backed offline action queue.
 *
 * When the user is offline, mutations are queued here instead of being sent
 * to the API.  On reconnection the App posts them to POST /api/sync which
 * replays them in chronological order.
 *
 * Deliberately no external dependency — the IDB API is wrapped manually to
 * keep the bundle minimal and avoid import issues in service-worker context.
 */

const DB_NAME = 'wastesite-offline'
const DB_VERSION = 1
const STORE_NAME = 'offline-actions'

export type OfflineActionType = 'CREATE' | 'UPDATE'

export interface OfflineAction {
  /** Client-generated UUID */
  id: string
  type: OfflineActionType
  /** Target API endpoint, e.g. "/api/waste-entries" */
  endpoint: string
  /** HTTP method to replay */
  method: 'POST' | 'PUT' | 'PATCH'
  /** Request body to send */
  body: Record<string, unknown>
  /** ISO 8601 timestamp */
  timestamp: string
}

// ── IndexedDB helpers ──────────────────────────────────────────────────────

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' })
        // Index by timestamp so we can replay in order
        store.createIndex('timestamp', 'timestamp', { unique: false })
      }
    }

    request.onsuccess = (event) => resolve((event.target as IDBOpenDBRequest).result)
    request.onerror = (event) => reject((event.target as IDBOpenDBRequest).error)
  })
}

function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDB().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode)
        const store = tx.objectStore(STORE_NAME)
        const request = fn(store)
        request.onsuccess = (event) => resolve((event.target as IDBRequest<T>).result)
        request.onerror = (event) => reject((event.target as IDBRequest<T>).error)
        tx.oncomplete = () => db.close()
        tx.onerror = () => {
          reject(tx.error)
          db.close()
        }
      }),
  )
}

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Enqueue an action to be replayed once the connection is restored.
 */
export function saveOfflineAction(action: OfflineAction): Promise<string> {
  return withStore<IDBValidKey>('readwrite', (store) => store.put(action)).then(
    (key) => String(key),
  )
}

/**
 * Return all queued actions, sorted oldest-first (by timestamp).
 */
export function getOfflineActions(): Promise<OfflineAction[]> {
  return openDB().then(
    (db) =>
      new Promise<OfflineAction[]>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly')
        const store = tx.objectStore(STORE_NAME)
        const index = store.index('timestamp')
        const request = index.getAll()
        request.onsuccess = (event) => {
          const result = (event.target as IDBRequest<OfflineAction[]>).result
          resolve(result.sort((a, b) => a.timestamp.localeCompare(b.timestamp)))
        }
        request.onerror = (event) => reject((event.target as IDBRequest).error)
        tx.oncomplete = () => db.close()
      }),
  )
}

/**
 * Remove all queued actions (call after a successful sync).
 */
export function clearOfflineActions(): Promise<void> {
  return withStore<undefined>('readwrite', (store) => store.clear())
}

/**
 * Remove a single action by id (call when only some actions succeed).
 */
export function removeOfflineAction(id: string): Promise<void> {
  return withStore<undefined>('readwrite', (store) => store.delete(id))
}

/**
 * How many actions are currently queued.
 */
export function getPendingCount(): Promise<number> {
  return withStore<number>('readonly', (store) => store.count())
}

/**
 * Build an OfflineAction from a fetch call that failed while offline.
 * Generates a client-side UUID without the `uuid` dep.
 */
export function buildOfflineAction(
  type: OfflineActionType,
  endpoint: string,
  method: 'POST' | 'PUT' | 'PATCH',
  body: Record<string, unknown>,
): OfflineAction {
  const id = crypto.randomUUID()
  return { id, type, endpoint, method, body, timestamp: new Date().toISOString() }
}
